<template>
  <section class="eventCard -hospitalDetail" :class="eventTypeClass">
    <div class="head">
      <template v-if="category">
        <img :src="categoryIcon" alt="" />
        <span>{{ category }}</span>
      </template>
    </div>
    <div class="container">
      <div class="eventDetail">
        <h3 class="eventDetail_heading" :class="{ noThumb: !event.thumbnail }">
          {{ event.title }}
        </h3>
        <p class="eventDetail_image" v-if="event.thumbnail">
          <img :src="event.thumbnail" alt="" />
        </p>
        <ul class="eventDetail_label">
          <li class="eventDetail_mainLabel">{{ mainEventLabel }}</li>
          <li v-for="castType in event.cast_type" :key="castType.value">
            {{ castType.label }}
          </li>
          <li v-for="filterType in event.filter_type" :key="filterType">
            {{ filterType === 'first' ? '初期研修' : '専門研修' }}
          </li>
        </ul>
        <dl class="eventDetail_data">
          <dt>日時</dt>
          <dd>{{ getEventTerm(event.term_start, event.term_end) }}</dd>
        </dl>
        <dl v-if="eventPlace" class="eventDetail_data">
          <dt>会場</dt>
          <dd>{{ eventPlace }}</dd>
        </dl>
        <ul class="eventDetail_tag" v-if="tags.length > 0">
          <li v-for="(tag, i) in tags" :key="i">
            <Tag :text="tag" check />
          </li>
        </ul>
      </div>

      <template v-if="latestSeminar.length > 0">
        <div class="seminar" v-for="(seminar, i) in latestSeminar" :key="i">
          <HeadingLv3 :text="seminar.title"></HeadingLv3>

          <p class="seminar_desc">
            {{ seminar.desciription }}
          </p>

          <div class="seminar_cnt">
            <p class="seminar_cnt_title">セミナー概要</p>
            <dl class="seminar_item">
              <dt>申込方法</dt>
              <dd>{{ seminar.howto }}</dd>
            </dl>
            <dl class="seminar_item">
              <dt>申込開始日</dt>
              <dd>{{ dateFormat(seminar.term_start) }}～</dd>
            </dl>
            <dl class="seminar_item">
              <dt>申込終了日</dt>
              <dd>{{ dateFormat(seminar.term_end) }}まで</dd>
            </dl>
            <dl class="seminar_item">
              <dt>定員</dt>
              <dd>{{ seminar.capacity }}</dd>
            </dl>
            <dl class="seminar_item">
              <dt>開催時間</dt>
              <dd v-html="seminar.time" />
            </dl>
          </div>
        </div>
      </template>

      <div class="card_btn">
        <Button
          label="詳細・予約"
          :href="`/events/detail/${event.id}`"
          :handleClick="() => $router.push(`/events/detail/${event.id}`)"
          secondary
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent } from '@vue/runtime-core'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import Tag from '@/components/elements/Tag.vue'
import Button from '@/components/elements/Button.vue'
import { dateFormat, getEventTerm } from '@/utils/commonMethods'

export default defineComponent({
  name: 'EventCardHospital',

  components: {
    HeadingLv3,
    Button,
    Tag
  },

  props: {
    event: {
      type: Object,
      default: () => ({})
    }
  },

  setup(props) {
    const eventType = computed(() => {
      if (props.event.type === 'オンライン' || props.event.type === 'online') {
        return 'オンライン'
      }
      return 'オフライン'
    })

    const mainEventLabel = computed(() => {
      if (
        props.event.event_category.find(
          (cat: { name: string }) => cat.name === 'キャリアガイダンス'
        )
      ) {
        return 'キャリアガイダンス'
      }
      return eventType.value
    })

    const categoryIcon = computed(() => {
      if (mainEventLabel.value === 'キャリアガイダンス') {
        return require('@/assets/images/icon/book-white.svg')
      }
      if (mainEventLabel.value === 'オンライン') {
        return require('@/assets/images/icon/pc-white.svg')
      }
      if (mainEventLabel.value === 'オフライン') {
        return require('@/assets/images/icon/hospital-02-white.svg')
      }
    })

    const eventTypeClass = computed(() => {
      if (mainEventLabel.value === 'キャリアガイダンス') return 'isCareer'
      if (mainEventLabel.value === 'オンライン') return 'isOnline'
      if (mainEventLabel.value === 'オフライン') return 'isOffline'
    })

    const eventPlace = computed(() => {
      if (eventType.value === 'オンライン' && props.event.place_name) {
        return props.event.place_name
      }
      if (eventType.value === 'オフライン') {
        return `${props.event.place_name}(${props.event.place_prefecture}${props.event.place_city})`
      }
      return ''
    })

    const tags = computed(() => {
      if (props.event.tags) return props.event.tags
      const res = []
      if (props.event.benefits) res.push('イベント特典あり')
      if (props.event.seminar && props.event.seminar.length > 0) {
        res.push('セミナーあり')
      }
      return res
    })

    const latestSeminar = computed(() => {
      if (props.event.seminar.length === 0) return []
      const lastItemIndex = props.event.seminar.length - 1
      return [props.event.seminar[lastItemIndex]]
    })

    const category = computed(() => {
      const categories: { name: string }[] = props.event.event_category
      if (categories.some((cat) => cat.name === 'キャリアガイダンス')) {
        return 'キャリアガイダンス'
      }
      return categories?.length ? categories[0].name : ''
    })

    return {
      dateFormat,
      getEventTerm,
      eventTypeClass,
      categoryIcon,
      mainEventLabel,
      eventPlace,
      category,
      tags,
      latestSeminar
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.container {
  padding: 30px 20px;
}
.eventCard {
  background-color: #fff;
  border: 1px solid var.$color-border-gray;
  border-radius: 5px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  & + & {
    margin-top: 16px;
  }
  &.isOnline {
    .head {
      background-color: #36c0f2;
    }
    .eventDetail_mainLabel {
      background-color: #36c0f2;
      color: #fff;
    }
  }
  &.isOffline {
    .head {
      background-color: var.$color-primary;
    }
    .eventDetail_mainLabel {
      background-color: var.$color-primary;
      color: #fff;
    }
  }
  &.isCareer {
    .head {
      background-color: var.$color-main;
    }
    .eventDetail_mainLabel {
      background-color: var.$color-main;
      color: #fff;
    }
  }
  &.-hospitalDetail {
    .container {
      position: relative;
    }

    @include mixins.media('sm') {
      .eventDetail_heading,
      .eventDetail_data {
        padding-right: 200px;
      }
      .eventDetail_image {
        width: 180px;
        top: 30px;
      }
    }
  }
}
.eventDetail {
  margin-bottom: 20px;
}
.eventDetail_heading {
  margin-top: -5px;
  margin-bottom: 10px;
  font-size: 1.8rem;
  font-weight: 500;
  line-height: 1.5;
  padding-right: 85px;
  &.noThumb {
    padding-right: 0;
  }
}
.head {
  background-color: var.$color-main;
  color: var.$color-white;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 22px;
  padding: 5px 0;
  font-weight: bold;
  height: 32px;
  letter-spacing: 0;
  img {
    height: 12px;
    margin-right: 4px;
  }
}

.eventDetail_image {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 70px;

  img {
    width: 100%;
  }
}
.eventDetail_label {
  display: flex;
  flex-wrap: wrap;

  li {
    display: inline-block;
    padding-right: 5px;
    padding-left: 5px;
    background-color: var.$color-accent-back;
    font-size: 1rem;
    line-height: 18px;
    margin-top: 5px;
    margin-right: 8px;
  }
}

.eventDetail_data {
  display: flex;
  font-size: 1.3rem;
  line-height: 1.46;
  margin-top: 10px;

  dt {
    margin-right: 30px;
    font-weight: 500;
    flex-shrink: 0;
  }
}

.eventDetail_tag {
  display: flex;
  flex-wrap: wrap;
  margin: 5px -2px -2px;

  > li {
    margin: 2px;
  }
}

.seminar {
  border-top: 1px solid var.$color-border-gray;
  border-bottom: 1px solid var.$color-border-gray;
  padding: 20px;
  margin-bottom: 20px;

  @include mixins.media() {
    padding-left: 0;
    padding-right: 0;
  }
}

.seminar_desc {
  margin-top: 16px;
  line-height: 1.8;
}

.seminar_cnt {
  margin-top: 16px;

  @include mixins.media() {
    display: flex;
    flex-wrap: wrap;
  }
}

.seminar_cnt_title {
  font-weight: 900;
  margin-bottom: 6px;

  @include mixins.media() {
    font-size: 1.6rem;
    flex-basis: 100%;
  }
}

.seminar_item {
  display: flex;
  font-size: 1.3rem;
  line-height: 1.5;

  @include mixins.media() {
    flex: 45% 0 0;
    font-size: 1.4rem;
    flex-direction: column;
    padding-right: 20px;
  }

  @include mixins.mediaMax() {
    &:nth-of-type(n + 2) {
      margin-top: 8px;
    }
  }

  @include mixins.media() {
    &:nth-of-type(n + 3) {
      margin-top: 12px;
    }
  }

  dt {
    font-weight: 500;
    flex: 84px 0 0;

    @include mixins.media() {
      flex: auto 0 0;
      margin-bottom: 4px;
    }
  }

  dd {
    color: #666;
    & ::v-deep a {
      color: var.$color-main-text;
    }
  }
}

.card_btn {
  width: 215px;
  max-width: 100%;
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
}
</style>
